import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AddCarbonComponent } from './add-carbon/add-carbon.component';
import { ShowcarbonComponent } from './showcarbon/showcarbon.component';
import { ChartOptions } from '../tips/charts/charts.component';
import { RequestService } from 'src/app/services/request.service';
import { AlertsService } from 'src/app/services/alerts.service';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-carbon-footprint',
  templateUrl: './carbon-footprint.component.html'
})
export class CarbonFootprintComponent implements OnInit, OnDestroy {
  carbonForm!: FormGroup;

  carbonFields: string[] = [
    'crop_type', 'size', 'energy_consumption', 'total_nitrogen',
    'fuel_consumption', 'ef_fuel', 'residues_amount', 'efresidue',
    'spraying_fuel_consumption', 'spraying_ef_fuel', 'total_spraying',
    'factor_spraying_emissions', 'rsequestration'
  ];

  dropdownFields: string[] = [
    'ef_fuel', 'efresidue', 'spraying_ef_fuel',
    'factor_spraying_emissions', 'rsequestration'
  ];

  dropdownOptions: { [key: string]: string[] } = {
    ef_fuel: ['1.5', '2.0', '2.5'],
    efresidue: ['0.2', '0.4', '0.6'],
    spraying_ef_fuel: ['0.1', '0.3', '0.5'],
    factor_spraying_emissions: ['0.4', '0.5', '0.6'],
    rsequestration: ['1.0', '1.2', '1.4']
  };

  savedDataList: any[] = [];
  selectedSoilData: any = null;
  chartsCropTypes: any[] = [];
  crop: any;
  selectedTabIndex: number = 0;
  savedCarbon: boolean = true;
  widgets: any[] = [];

  carbonPieChartOptions: Partial<ChartOptions> = { series: [], chart: { type: 'pie' } };
  carbonBarChartOptions: Partial<ChartOptions> = { series: [], chart: { type: 'bar' } };
  carbonBarVerticalChartOptions: Partial<ChartOptions> = { series: [], chart: { type: 'bar' } };
  langChangeSub!: Subscription;

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private req: RequestService<any>,
    private alert: AlertsService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.getSavedCarbon();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      if (this.savedDataList?.length) {
        const data = this.savedDataList[0];
        this.generateCharts(data);
        if (!this.savedCarbon) {
          this.req.getCarbonFootrpintTotal().subscribe(res => {
            this.generateCarbonBarVerticalChart(res);
          });
        }
      }
    });
  }


  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  initializeForm(): void {
    this.carbonForm = this.fb.group({});
    this.carbonFields.forEach(field => {
      this.carbonForm.addControl(field, this.fb.control(null));
    });
  }

  addCarbonButton(): void {
    const dialogRef = this.dialog.open(AddCarbonComponent);
    dialogRef.componentInstance.carbonSaved.subscribe(() => {
      this.getSavedCarbon();
    });

  }

  addCarbonEntry(entry: any): void {
    this.savedDataList.push(entry);
  }

  fillSoilDataForm(data: any): void {
    this.selectedSoilData = data;
    this.carbonFields.forEach(field => {
      this.carbonForm.get(field)?.setValue(data[field]);
    });
  }

  getSavedCarbon(): void {
    this.req.getCarbonFootrpint().subscribe(res => {
      this.savedDataList = res;
      this.chartsCropTypes = res;
      this.generateCharts(res[0]);
    });
  }

  onTabChange(event: any): void {
    this.selectedTabIndex = event.index;
    this.savedCarbon = this.selectedTabIndex === 0;
    const apiCall = this.savedCarbon
      ? this.req.getCarbonFootrpint()
      : this.req.getCarbonFootrpintTotal();
    apiCall.subscribe(res => {
      const data = this.savedCarbon ? res[0] : res;
      this.generateCharts(data);
      if (!this.savedCarbon) this.generateCarbonBarVerticalChart(res);
    });
  }

  showRow(data: any): void {
    this.dialog.open(ShowcarbonComponent, { data });
  }

  deleteRow(data: any) {
    this.alert.confirmDelete().then(result => {
      if (result.isConfirmed) {
        this.req.getCarbonFootrpintDelete(data._id).subscribe({
          next: () => {
            this.alert.success('Your file has been deleted.', 'Deleted!');
            this.dialog.afterAllClosed.subscribe(() => {
              this.initializeForm();
              this.getSavedCarbon();
            });
          },
          error: (error) => {
            console.error('Delete failed:', error);
            this.alert.error(error?.message || 'Something went wrong while deleting.');
          }
        });
      }
    });
  }

  rowSaved(data: any): void {
    this.generateCharts(data);
  }

  generateCharts(data: any): void {
    this.generateCarbonPieChart(data);
    this.generateCarbonBarChart(data);
    this.widgetsData(data);
  }

  generateCarbonPieChart(data: any): void {
    const result = data?.results;
    if (!result) return;

    this.translate.get([
      'CARBON.CATEGORY_LABELS_FERTILIZER',
      'CARBON.CATEGORY_LABELS_FUEL',
      'CARBON.CATEGORY_LABELS_IRRIGATION',
      'CARBON.CATEGORY_LABELS_RESIDUES',
      'CARBON.CATEGORY_LABELS_SPRAYING',
      'CARBON.EMISSIONS_BREAKDOWN',
      'CARBON.CHART_BAR_TITLE_Y',
      'CARBON.CHART_BAR_TITLE_X',
      'CARBON.CHART_BAR_TITLE'
    ]).subscribe(trans => {
      const labels = [
        trans['CARBON.CATEGORY_LABELS_FERTILIZER'],
        trans['CARBON.CATEGORY_LABELS_FUEL'],
        trans['CARBON.CATEGORY_LABELS_IRRIGATION'],
        trans['CARBON.CATEGORY_LABELS_RESIDUES'],
        trans['CARBON.CATEGORY_LABELS_SPRAYING']
      ];

      const series = [
        result.fert_emissions,
        result.fuel_emissions,
        result.irrigation_emissions,
        result.residues_emissions,
        result.spraying_emissions
      ];

      this.carbonPieChartOptions = {
        series,
        chart: { type: 'pie', height: 350, width: 400 },
        labels,
        title: {
          text: `${trans['CARBON.CHART_BAR_TITLE']} ${data.crop_type}`,
          align: 'center',
          style: { fontSize: '10px', fontWeight: 'bold' }
        },
        colors: ['#4ade80', '#fb7185', '#38bdf8', '#facc15', '#8d99ae'],
        legend: {
          position: 'bottom',
          fontSize: '10px',
          labels: { colors: ['#777'] },
          markers: { width: 5, height: 5, radius: 50 }
        },
        dataLabels: {
          enabled: true,
          formatter: (val: number) => `${val.toFixed(1)}%`,
          style: { fontSize: '8px', fontWeight: 'bold', colors: ['white'] }
        }
      };
    });
  }

  generateCarbonBarChart(data: any): void {
    const result = data?.results;
    if (!result) return;

    this.translate.get([
      'CARBON.CATEGORY_LABELS_FERTILIZER',
      'CARBON.CATEGORY_LABELS_FUEL',
      'CARBON.CATEGORY_LABELS_IRRIGATION',
      'CARBON.CATEGORY_LABELS_RESIDUES',
      'CARBON.CATEGORY_LABELS_SPRAYING',
      'CARBON.CHART_BAR_TITLE_Y',
      'CARBON.CHART_BAR_TITLE_X',
      'CARBON.CHART_BAR_TITLE'
    ]).subscribe(trans => {
      const labels = [
        trans['CARBON.CATEGORY_LABELS_FERTILIZER'],
        trans['CARBON.CATEGORY_LABELS_FUEL'],
        trans['CARBON.CATEGORY_LABELS_IRRIGATION'],
        trans['CARBON.CATEGORY_LABELS_RESIDUES'],
        trans['CARBON.CATEGORY_LABELS_SPRAYING']
      ];

      const values = [
        -result.calculate_rsequestration,
        result.fert_emissions,
        result.fuel_emissions,
        result.irrigation_emissions,
        result.residues_emissions,
        result.spraying_emissions
      ];

      this.carbonBarChartOptions = {
        series: [{ name: trans['CARBON.CHART_BAR_TITLE_Y'], data: values }],
        chart: { type: 'bar', height: 350, width: 600, toolbar: { show: false } },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 6,
            barHeight: '70%',
            colors: {
              ranges: [
                { from: -99999, to: 0, color: '#facc15' },
                { from: 0.01, to: 99999, color: '#4ade80' }
              ]
            }
          }
        },
        xaxis: {
          categories: labels,
          title: { text: trans['CARBON.CHART_BAR_TITLE_X'] },
          labels: { formatter: (val: any) => Math.round(val).toString() }
        },
        yaxis: { title: { text: trans['CARBON.CHART_BAR_TITLE_Y'] } },
        dataLabels: {
          enabled: true,
          formatter: (val: number) => val.toFixed(2),
          style: { fontSize: '10px', fontWeight: 'bold', colors: ['#121814'] }
        },
        title: {
          text: `${trans['CARBON.CHART_BAR_TITLE']}${data.crop_type}`,
          align: 'center',
          style: { fontSize: '10px', fontWeight: 'bold' }
        }
      };
    });
  }


  generateCarbonBarVerticalChart(data: any): void {
    const crops = data?.by_crop || [];
    if (!crops.length) return;
    this.translate.get([
      'CARBON.CHART_BAR_TITLE',
      'CARBON.CHART_BAR_TITLE_Y',
      'CARBON.CHART_BAR_TITLE_X'
    ]).subscribe(trans => {
      const labels = crops.map((el: any) => el.crop_type);
      const values = crops.map((el: any) => el.total_emissions);

      this.carbonBarVerticalChartOptions = {
        series: [{ name: trans['CARBON.CHART_BAR_TITLE_Y'], data: values }],
        chart: { type: 'bar', height: 350, width: 450, toolbar: { show: false } },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 6,
            barHeight: '70%',
            colors: {
              ranges: [
                { from: -99999, to: 0, color: '#facc15' },
                { from: 0.01, to: 99999, color: '#4ade80' }
              ]
            }
          }
        },
        xaxis: {
          categories: labels,
          title: { text: trans['CARBON.CHART_BAR_TITLE_X'] }
        },
        yaxis: {
          title: { text: trans['CARBON.CHART_BAR_TITLE_Y'] },
          labels: {
            formatter: (val: any) => Math.round(val).toString()
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val: number) => val.toFixed(2),
          style: {
            fontSize: '10px',
            fontWeight: 'bold',
            colors: ['#121814']
          }
        },
        title: {
          text: `${trans['CARBON.CHART_BAR_TITLE']}${this.crop?.crop_type ?? data.crop_type}`,
          align: 'center',
          style: {
            fontSize: '10px',
            fontWeight: 'bold'
          }
        }
      };
    });
  }


  widgetsData(data: any): void {
    if (!data) return;
    this.widgets = [{
      osde_year: data.osde_year || 0,
      total_emissions: data.results?.total_emissions?.toFixed(0) || 0,
      size: data.size?.toFixed(0) || 0,
      total_nitrogen: data.total_nitrogen?.toFixed(0) || 0,
      energy_consumption: data.energy_consumption?.toFixed(0) || 0,
      fuel_consumption: data.fuel_consumption?.toFixed(0) || 0,
      ef_fuel: data.ef_fuel?.toFixed(0) || 0,
      residues_amount: data.residues_amount?.toFixed(0) || 0,
      efresidue: data.efresidue?.toFixed(0) || 0
    }];
  }
}
